// ============================================================================
// useRoleTabs — abas/permissões do FUNCIONÁRIO (data-driven), com override
// individual opcional.
// ----------------------------------------------------------------------------
// Base: o CARGO (employee_types) define abas, detalhes do projeto, capacidades
// e dashboards. Cada FUNCIONÁRIO pode ter um override individual em
// users/{uid}.perms = { custom: true, tabs, projectDetails, capabilities,
// dashboards }. Quando `custom` é true, o override vence; senão, herda do cargo.
//
// Os hooks recebem `perms` (o objeto users/{uid}.perms já carregado pelo
// AuthContext). Sem `perms`, comportam-se como antes (só cargo).
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { safeOnSnapshot } from "../lib/safeSnapshot";
import { db } from "../firebaseConfig";
import { resolveRoleTabs } from "../lib/employeeTabCatalog";
import { resolveProjectDetailsConfig } from "../lib/projectDetailsCatalog";
import { resolveDashboards, hasAnyDashboard } from "../lib/dashboardCatalog";
import { permsForRole } from "../lib/roles";

const findRoleDoc = (docs, employeeRole) =>
  docs.map((d) => d.data()).find((t) => t.value === employeeRole);

// Fonte efetiva de config: override individual (se custom) ou o doc do cargo.
const effectiveSource = (roleDoc, perms) =>
  perms && perms.custom ? perms : roleDoc;

// Aba sintética de Dashboard (injetada quando há dashboard habilitado).
const DASHBOARD_TAB = {
  key: "dashboard",
  route: "/employee/dashboard",
  label: "Dashboard",
  icon: "FaChartPie",
  columns: [],
  filters: [],
};
const withDashboard = (tabs, src) =>
  hasAnyDashboard(src?.dashboards) ? [DASHBOARD_TAB, ...tabs] : tabs;

// Hook reativo: abas resolvidas do colaborador (cargo + override) + loading.
export const useRoleTabs = (employeeRole, perms) => {
  const [roleDoc, setRoleDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeRole) {
      setRoleDoc(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = safeOnSnapshot(
      collection(db, "employee_types"),
      (snap) => {
        setRoleDoc(findRoleDoc(snap.docs, employeeRole) || null);
        setLoading(false);
      },
      {
        label: "employee_types",
        onError: (err) => {
          console.error("Erro ao carregar abas do cargo:", err);
          setRoleDoc(null);
          setLoading(false);
        },
      }
    );
    return () => unsub();
  }, [employeeRole]);

  const tabs = useMemo(() => {
    const src = effectiveSource(roleDoc, perms);
    return withDashboard(resolveRoleTabs(src?.tabs), src);
  }, [roleDoc, perms]);

  return { tabs, loading };
};

// Versão one-shot (login). Aceita o override do colaborador, se houver.
export const fetchRoleTabs = async (employeeRole, perms) => {
  if (!employeeRole) return [];
  try {
    const snap = await getDocs(collection(db, "employee_types"));
    const roleDoc = findRoleDoc(snap.docs, employeeRole);
    const src = effectiveSource(roleDoc, perms);
    return withDashboard(resolveRoleTabs(src?.tabs), src);
  } catch (err) {
    console.error("Erro ao buscar abas do cargo:", err);
    return [];
  }
};

// Config de visibilidade da tela de Detalhes do Projeto (cargo + override).
export const useRoleProjectDetailsConfig = (employeeRole, perms) => {
  const [roleDoc, setRoleDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeRole) {
      setRoleDoc(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = safeOnSnapshot(
      collection(db, "employee_types"),
      (snap) => {
        setRoleDoc(findRoleDoc(snap.docs, employeeRole) || null);
        setLoading(false);
      },
      {
        label: "employee_types",
        onError: (err) => {
          console.error("Erro ao carregar config de detalhes do cargo:", err);
          setRoleDoc(null);
          setLoading(false);
        },
      }
    );
    return () => unsub();
  }, [employeeRole]);

  const config = useMemo(
    () => resolveProjectDetailsConfig(effectiveSource(roleDoc, perms)?.projectDetails),
    [roleDoc, perms]
  );

  return { ...config, loading };
};

// Multi-cargo: UNIÃO das ações liberadas (allowedActions) entre vários cargos.
// Usado na execução de etapa (CompareEdit): o colaborador age numa etapa de
// QUALQUER cargo dele (ex.: certificar na etapa do Gerente sendo Linguista).
// Para cada cargo usa o override POR CARGO (permsForRole) ou a config do cargo.
export const useRoleAllowedActionsUnion = (roles, user) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const rolesKey = Array.isArray(roles) ? roles.filter(Boolean).join("|") : String(roles || "");

  useEffect(() => {
    if (!rolesKey) {
      setDocs([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = safeOnSnapshot(
      collection(db, "employee_types"),
      (snap) => {
        setDocs(snap.docs.map((d) => d.data()));
        setLoading(false);
      },
      {
        label: "employee_types",
        onError: (err) => {
          console.error("Erro ao carregar cargos (união):", err);
          setDocs([]);
          setLoading(false);
        },
      }
    );
    return () => unsub();
  }, [rolesKey]);

  const allowedActions = useMemo(() => {
    const set = new Set();
    (Array.isArray(roles) ? roles : []).forEach((role) => {
      const perms = permsForRole(user, role);
      const roleDoc = docs.find((t) => t.value === role);
      const src = perms && perms.custom ? perms : roleDoc;
      resolveProjectDetailsConfig(src?.projectDetails).allowedActions.forEach((a) => set.add(a));
    });
    return Array.from(set);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, rolesKey, user]);

  return { allowedActions, loading };
};

// Capacidades operacionais do cargo/colaborador (ex.: controlar o workflow).
export const useRoleCapabilities = (employeeRole, perms) => {
  const [roleDoc, setRoleDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeRole) {
      setRoleDoc(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = safeOnSnapshot(
      collection(db, "employee_types"),
      (snap) => {
        setRoleDoc(findRoleDoc(snap.docs, employeeRole) || null);
        setLoading(false);
      },
      {
        label: "employee_types",
        onError: (err) => {
          console.error("Erro ao carregar capacidades do cargo:", err);
          setRoleDoc(null);
          setLoading(false);
        },
      }
    );
    return () => unsub();
  }, [employeeRole]);

  const caps = useMemo(() => {
    const c = effectiveSource(roleDoc, perms)?.capabilities;
    return {
      controlWorkflow: c?.controlWorkflow !== false,
      viewProjectDetails: c?.viewProjectDetails !== false,
      // Controlar a fila de jobs: permissão especial, desligada por padrão.
      controlQueue: c?.controlQueue === true,
      // Atuar em qualquer arquivo sem seguir a ordem da fila (desligada por padrão).
      bypassQueueOrder: c?.bypassQueueOrder === true,
    };
  }, [roleDoc, perms]);

  return { ...caps, loading };
};

// Multi-cargo: pode ver os Detalhes do Projeto se QUALQUER cargo do colaborador
// tiver a capacidade ligada (default ligado). Usado na tela de etapa
// (CompareEdit), onde o colaborador age em etapas de vários cargos.
export const useRoleViewProjectDetailsUnion = (roles, user) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const rolesKey = Array.isArray(roles)
    ? roles.filter(Boolean).join("|")
    : String(roles || "");

  useEffect(() => {
    if (!rolesKey) {
      setDocs([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = safeOnSnapshot(
      collection(db, "employee_types"),
      (snap) => {
        setDocs(snap.docs.map((d) => d.data()));
        setLoading(false);
      },
      {
        label: "employee_types",
        onError: (err) => {
          console.error("Erro ao carregar capacidades (união):", err);
          setDocs([]);
          setLoading(false);
        },
      }
    );
    return () => unsub();
  }, [rolesKey]);

  const canView = useMemo(() => {
    const list = Array.isArray(roles) ? roles.filter(Boolean) : [];
    if (!list.length) return false;
    return list.some((role) => {
      const perms = permsForRole(user, role);
      const roleDoc = docs.find((t) => t.value === role);
      const src = perms && perms.custom ? perms : roleDoc;
      return src?.capabilities?.viewProjectDetails !== false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, rolesKey, user]);

  return { canView, loading };
};

// Multi-cargo: pode atuar FORA da ordem da fila se QUALQUER cargo do colaborador
// tiver a capacidade `bypassQueueOrder` ligada (desligada por padrão). Usado na
// tela de etapa (CompareEdit) para liberar a conclusão de qualquer arquivo.
export const useRoleBypassQueueUnion = (roles, user) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const rolesKey = Array.isArray(roles)
    ? roles.filter(Boolean).join("|")
    : String(roles || "");

  useEffect(() => {
    if (!rolesKey) {
      setDocs([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = safeOnSnapshot(
      collection(db, "employee_types"),
      (snap) => {
        setDocs(snap.docs.map((d) => d.data()));
        setLoading(false);
      },
      {
        label: "employee_types",
        onError: (err) => {
          console.error("Erro ao carregar capacidades (fila/união):", err);
          setDocs([]);
          setLoading(false);
        },
      }
    );
    return () => unsub();
  }, [rolesKey]);

  const canBypass = useMemo(() => {
    const list = Array.isArray(roles) ? roles.filter(Boolean) : [];
    if (!list.length) return false;
    return list.some((role) => {
      const perms = permsForRole(user, role);
      const roleDoc = docs.find((t) => t.value === role);
      const src = perms && perms.custom ? perms : roleDoc;
      return src?.capabilities?.bypassQueueOrder === true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, rolesKey, user]);

  return { canBypass, loading };
};

// Dashboards liberados (cargo + override): blocos e escopo.
export const useRoleDashboards = (employeeRole, perms) => {
  const [roleDoc, setRoleDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeRole) {
      setRoleDoc(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = safeOnSnapshot(
      collection(db, "employee_types"),
      (snap) => {
        setRoleDoc(findRoleDoc(snap.docs, employeeRole) || null);
        setLoading(false);
      },
      {
        label: "employee_types",
        onError: (err) => {
          console.error("Erro ao carregar dashboards do cargo:", err);
          setRoleDoc(null);
          setLoading(false);
        },
      }
    );
    return () => unsub();
  }, [employeeRole]);

  const config = useMemo(
    () => resolveDashboards(effectiveSource(roleDoc, perms)?.dashboards),
    [roleDoc, perms]
  );

  return { ...config, loading };
};

// Controle de acesso a uma aba específica (considera cargo + override).
export const useTabAccess = (employeeRole, tabKey, perms) => {
  const { tabs, loading } = useRoleTabs(employeeRole, perms);
  if (loading) return { status: "loading" };
  if (tabs.some((t) => t.key === tabKey)) return { status: "ok", tabs };
  if (tabs.length > 0) {
    return { status: "redirect", redirectTo: tabs[0].route, tabs };
  }
  return { status: "empty", tabs };
};
